import {
  type PnidEntry,
  pnidEntrySchema,
} from 'features/knowledgeDocumentViewer/graph/pnidSchemas'
import type { RolodexFilterType } from 'types'
import { z } from 'zod'

import { PnidGraph } from './graphIndex'
import { missingGraphReferences } from './graphReferences'
import { resolveOffPageConnectorReference } from './offPageConnectorGraph'

export type GraphScope = {
  organization: string
  site: string
  'pipeline-config': string
}
export type GraphLoadQuery = {
  type: string[]
  tags: Record<string, string>
  limit: number | null
  offset?: number
  ids?: string[]
  filters?: RolodexFilterType[]
  order_by?: { field: string; descending: boolean }[]
  advanced_filter?: {
    operator: 'or'
    filters: {
      field: 'data'
      key: 'source_node_id' | 'target_node_id' | 'node_id'
      op: 'in'
      value: string[]
    }[]
  }
}
export type GraphTransport = (query: GraphLoadQuery) => Promise<unknown>
const GRAPH_TYPES = [
  'pnid-node',
  'pnid-edge',
  'pnid-node-overlay',
  'pnid-extraction-progress',
]
const responseSchema = z.array(z.unknown())
const wrapperSchema = z.looseObject({ entry: pnidEntrySchema })

type GraphLoadContext = {
  scope: GraphScope
  load: GraphTransport
  entries: Map<string, PnidEntry>
}

async function fetchEntries(
  { scope, load, entries }: GraphLoadContext,
  query: GraphLoadQuery,
  accept?: (entry: PnidEntry) => boolean
): Promise<void> {
  const rows = responseSchema.parse(await load(query))
  for (const row of rows) {
    const parsed = wrapperSchema.safeParse(row)
    if (!parsed.success) continue
    const entry = parsed.data.entry
    if (
      entry.tags.organization !== scope.organization ||
      entry.tags.site !== scope.site ||
      entry.tags['pipeline-config'] !== scope['pipeline-config'] ||
      (query.tags['system-document-page'] !== undefined &&
        entry.tags['system-document-page'] !==
          query.tags['system-document-page']) ||
      (query.ids !== undefined && !query.ids.includes(entry.id)) ||
      (accept !== undefined && !accept(entry))
    )
      continue
    entries.set(entry.id, entry)
  }
}

async function loadPageEntries(
  pageId: string,
  context: GraphLoadContext
): Promise<void> {
  await fetchEntries(context, {
    type: GRAPH_TYPES,
    tags: {
      ...context.scope,
      sandbox: 'sandbox',
      'system-document-page': pageId,
    },
    limit: null,
  })
}

function getPageNodeIds(
  pageId: string,
  entries: Map<string, PnidEntry>
): Set<string> {
  const pageNodeIds = new Set<string>()
  for (const entry of entries.values()) {
    if (
      entry.type === 'pnid-node' &&
      entry.tags['system-document-page'] === pageId
    )
      pageNodeIds.add(entry.id)
    if (entry.type === 'pnid-node-overlay') pageNodeIds.add(entry.data.node_id)
  }
  return pageNodeIds
}

function isPageIncidentEdge(
  entry: PnidEntry,
  pageId: string,
  pageNodeIds: Set<string>
): boolean {
  return (
    entry.type === 'pnid-edge' &&
    (pageNodeIds.has(entry.data.source_node_id) ||
      (entry.data.type === 'dexpi' &&
        pageNodeIds.has(entry.data.target_node_id))) &&
    (entry.tags['system-document-page'] === null ||
      entry.tags['system-document-page'] === undefined ||
      entry.tags['system-document-page'] === pageId)
  )
}

function isReferencedConnectorEdge(
  entry: PnidEntry,
  sourceIds: Set<string>
): boolean {
  return (
    entry.type === 'pnid-edge' &&
    entry.data.type === 'dexpi' &&
    sourceIds.has(entry.data.source_node_id) &&
    entry.data.dexpi_relationship_field === 'ReferencedConnector'
  )
}

type PageNodes = {
  pageId: string
  nodeIds: Set<string>
  idToReferencedConnector: Map<string, PnidEntry>
}

async function loadIncidentEdges(
  { pageId, nodeIds, idToReferencedConnector }: PageNodes,
  context: GraphLoadContext
): Promise<void> {
  const accept = (entry: PnidEntry): boolean => {
    if (isPageIncidentEdge(entry, pageId, nodeIds)) return true
    if (isReferencedConnectorEdge(entry, nodeIds))
      idToReferencedConnector.set(entry.id, entry)
    return false
  }
  const ids = [...nodeIds]
  if (!ids.length) return
  await fetchEntries(
    context,
    {
      type: ['pnid-edge'],
      tags: { ...context.scope, sandbox: 'sandbox' },
      limit: null,
      advanced_filter: {
        operator: 'or',
        filters: [
          { field: 'data', key: 'source_node_id', op: 'in', value: ids },
          { field: 'data', key: 'target_node_id', op: 'in', value: ids },
        ],
      },
    },
    accept
  )
}

async function hydrateMissingReferences(
  context: GraphLoadContext
): Promise<void> {
  const attempted = new Set<string>()
  while (true) {
    const missing = missingGraphReferences([
      ...context.entries.values(),
    ]).filter((id) => !attempted.has(id))
    if (!missing.length) break
    missing.forEach((id) => attempted.add(id))
    await fetchEntries(context, {
      type: GRAPH_TYPES,
      tags: { ...context.scope, sandbox: 'sandbox' },
      limit: null,
      ids: missing,
    })
  }
}

async function hydrateOffPageConnectorReferences(
  { pageId, nodeIds, idToReferencedConnector }: PageNodes,
  context: GraphLoadContext
): Promise<void> {
  const graph = PnidGraph.fromEntries(pageId, [...context.entries.values()])
  const referenceIds = new Set<string>()
  for (const node of graph.getNodes()) {
    if (!node.overlays.length) continue
    const reference = resolveOffPageConnectorReference(node)
    if (
      reference?.data.dexpi_class ===
      'Plant.Piping.PipeOffPageConnectorObjectReference'
    )
      referenceIds.add(reference.id)
  }
  for (const edge of idToReferencedConnector.values())
    if (isReferencedConnectorEdge(edge, referenceIds))
      context.entries.set(edge.id, edge)
  const ids = [...referenceIds].filter((id) => !nodeIds.has(id))
  const offPageIds = new Set(ids)
  if (ids.length)
    await fetchEntries(
      context,
      {
        type: ['pnid-edge'],
        tags: { ...context.scope, sandbox: 'sandbox' },
        limit: null,
        filters: [
          { field: 'data', key: 'source_node_id', op: 'in', value: ids },
        ],
      },
      (entry) => isReferencedConnectorEdge(entry, offPageIds)
    )
  await hydrateMissingReferences(context)
}

export async function loadPageGraph(
  pageId: string,
  scope: GraphScope,
  load: GraphTransport
): Promise<PnidGraph> {
  const entries = new Map<string, PnidEntry>()
  const context = { scope, load, entries }
  await loadPageEntries(pageId, context)
  const page = {
    pageId,
    nodeIds: getPageNodeIds(pageId, entries),
    idToReferencedConnector: new Map<string, PnidEntry>(),
  }
  await loadIncidentEdges(page, context)
  await hydrateMissingReferences(context)
  await hydrateOffPageConnectorReferences(page, context)
  return PnidGraph.fromEntries(pageId, [...entries.values()])
}
